/**
 * The staged selection and everything that changes it: seeding from the
 * saved picks, tap, consent for a pending spread, move, remove, the full
 * round refusal, save through `/pick/submit`, and the discard confirm (S3,
 * S4, S7). The route leave guard is added by ISSUE-973's WI-17.
 */
import { usePickSubmit } from "@sportsmet/contest-sdk/private";
import { confirmDestructive } from "@sportsmet/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import {
  isSubmitEnabled,
  resolvePrimaryLabel,
  type typeBoardData,
  type typePickTile,
} from "../pick/pickBoardModel";
import {
  PICK_CONFIRMED_MANY_NOTE,
  PICK_CONFIRMED_NOTE,
  PICK_DISCARD_BODY,
  PICK_DISCARD_CANCEL_LABEL,
  PICK_DISCARD_CONFIRM_LABEL,
  PICK_DISCARD_TITLE,
} from "../pick/pickCopy";
import {
  buildPickSubmitPayload,
  classifyPickSubmitError,
  type typePickSelection,
} from "../pick/pickSubmitModel";
import { SURVIVOR_ROUND_FULL_NOTE } from "./survivorGameCopy";
import {
  isSelectionChanged,
  moveSelectionToPriority,
  removeSelection,
  selectionsFromSavedPicks,
  toggleSelection,
} from "./survivorGameModel";
import type { typeSurvivorPrimaryAction } from "./SurvivorStatusBar";
import type { typeSurvivorGameView } from "./useSurvivorGameData";
import { useSurvivorLeaveGuard } from "./useSurvivorLeaveGuard";

export type typePendingConsent = {
  eventId: string;
  teamId: string;
  teamName: string;
};

export type typeSurvivorPickSelection = {
  selections: typePickSelection[];
  isDirty: boolean;
  inlineNote: string | null;
  pendingConsent: typePendingConsent | null;
  primaryAction: typeSurvivorPrimaryAction | null;
  selectSide: (input: { eventId: string; teamId: string }) => void;
  confirmConsent: () => void;
  cancelConsent: () => void;
  movePick: (input: { eventId: string; priority: number }) => void;
  removePick: (eventId: string) => void;
  resetToSaved: () => void;
  confirmDiscardIfDirty: () => Promise<boolean>;
};

/** The event and team a tile side is found by, mirroring
 * `PickSheetContainer.tsx`'s `findTileSide`. */
function findTileSide(
  tiles: readonly typePickTile[],
  input: { eventId: string; teamId: string },
): { isSpreadPending: boolean; teamName: string } | null {
  const tile = tiles.find((candidate) => candidate.eventId === input.eventId);
  if (!tile) return null;
  if (tile.home.teamId === input.teamId) return tile.home;
  if (tile.away.teamId === input.teamId) return tile.away;
  return null;
}

export function useSurvivorPickSelection(input: {
  poolId: string;
  contestId: string;
  roundId: string;
  entryId: string;
  boardData: typeBoardData | null;
  view: typeSurvivorGameView;
  tiles: readonly typePickTile[];
}): typeSurvivorPickSelection {
  const [selections, setSelections] = useState<typePickSelection[]>([]);
  const [inlineNote, setInlineNote] = useState<string | null>(null);
  const [pendingConsent, setPendingConsent] =
    useState<typePendingConsent | null>(null);

  const queryClient = useQueryClient();
  const pickSubmit = usePickSubmit();
  const requestIdRef = useRef<string>(crypto.randomUUID());

  const seededKeyRef = useRef<string>("");
  useEffect(() => {
    const boardData = input.boardData;
    if (boardData === null) return;
    const key = `${input.entryId}|${input.roundId}`;
    if (seededKeyRef.current === key) return;
    seededKeyRef.current = key;
    setSelections(selectionsFromSavedPicks(boardData.myPicks));
    setInlineNote(null);
  }, [input.boardData, input.entryId, input.roundId]);

  const isDirty =
    input.boardData !== null &&
    isSelectionChanged({ selections, myPicks: input.boardData.myPicks });

  const resetToSaved = useCallback(() => {
    setSelections(selectionsFromSavedPicks(input.boardData?.myPicks ?? []));
    setInlineNote(null);
  }, [input.boardData]);

  useSurvivorLeaveGuard({ isDirty, onDiscard: resetToSaved });

  function selectSide(sideInput: { eventId: string; teamId: string }): void {
    if (input.view.kind !== "open") return;
    const side = findTileSide(input.tiles, sideInput);
    if (side === null) return;
    const alreadySelected = selections.some(
      (selection) =>
        selection.eventId === sideInput.eventId &&
        selection.teamId === sideInput.teamId,
    );
    if (!alreadySelected && side.isSpreadPending) {
      setPendingConsent({
        eventId: sideInput.eventId,
        teamId: sideInput.teamId,
        teamName: side.teamName,
      });
      return;
    }
    const result = toggleSelection({
      selections,
      eventId: sideInput.eventId,
      teamId: sideInput.teamId,
      acceptsPendingSpread: false,
      requiredPickCount: input.view.requiredPickCount,
    });
    setSelections(result.selections);
    setInlineNote(result.refusedBecauseFull ? SURVIVOR_ROUND_FULL_NOTE : null);
  }

  function confirmConsent(): void {
    if (pendingConsent === null || input.view.kind !== "open") return;
    const result = toggleSelection({
      selections,
      eventId: pendingConsent.eventId,
      teamId: pendingConsent.teamId,
      acceptsPendingSpread: true,
      requiredPickCount: input.view.requiredPickCount,
    });
    setSelections(result.selections);
    setInlineNote(result.refusedBecauseFull ? SURVIVOR_ROUND_FULL_NOTE : null);
    setPendingConsent(null);
  }

  function cancelConsent(): void {
    setPendingConsent(null);
  }

  function movePick(moveInput: { eventId: string; priority: number }): void {
    if (input.view.kind !== "open") return;
    setSelections(
      moveSelectionToPriority({
        selections,
        eventId: moveInput.eventId,
        priority: moveInput.priority,
      }),
    );
    setInlineNote(null);
  }

  function removePick(eventId: string): void {
    if (input.view.kind !== "open") return;
    setSelections(removeSelection({ selections, eventId }));
    setInlineNote(null);
  }

  async function submit(): Promise<void> {
    setInlineNote(null);
    const payload = buildPickSubmitPayload({
      poolId: input.poolId,
      contestId: input.contestId,
      roundId: input.roundId,
      entryId: input.entryId,
      requestId: requestIdRef.current,
      selections,
    });
    const result = await pickSubmit.mutateAsync(payload);
    const outcome = classifyPickSubmitError(result);
    if (outcome.kind === "ok") {
      requestIdRef.current = crypto.randomUUID();
      setInlineNote(
        input.view.kind === "open" && input.view.requiredPickCount > 1
          ? PICK_CONFIRMED_MANY_NOTE
          : PICK_CONFIRMED_NOTE,
      );
      return;
    }
    if (outcome.kind === "inline") {
      if (outcome.code === "CONFLICT")
        requestIdRef.current = crypto.randomUUID();
      setInlineNote(outcome.note);
      void queryClient.invalidateQueries({
        queryKey: ["post:round:roundGetBoard", "svc:contest-api"],
      });
      return;
    }
    showApiError(result);
  }

  const primaryAction: typeSurvivorPrimaryAction | null =
    input.view.kind === "open"
      ? {
          label: resolvePrimaryLabel({
            requiredPickCount: input.view.requiredPickCount,
            canEdit: input.view.canEdit,
          }),
          enabled:
            isSubmitEnabled({
              phase: input.view,
              selectedCount: selections.length,
            }) && isDirty,
          isSubmitting: pickSubmit.isPending,
          onPress: () => {
            void submit();
          },
        }
      : null;

  const confirmDiscardIfDirty = useCallback(async (): Promise<boolean> => {
    if (!isDirty) return true;
    const isConfirmed = await confirmDestructive({
      title: PICK_DISCARD_TITLE,
      body: PICK_DISCARD_BODY,
      confirmLabel: PICK_DISCARD_CONFIRM_LABEL,
      cancelLabel: PICK_DISCARD_CANCEL_LABEL,
    });
    if (!isConfirmed) return false;
    resetToSaved();
    return true;
  }, [isDirty, resetToSaved]);

  return {
    selections,
    isDirty,
    inlineNote,
    pendingConsent,
    primaryAction,
    selectSide,
    confirmConsent,
    cancelConsent,
    movePick,
    removePick,
    resetToSaved,
    confirmDiscardIfDirty,
  };
}
